import type { ApiClient } from '@deskworks/client';
import type { JoinContext } from '@deskworks/domain';
import type { EndReason, MeetingBackend } from './engine.js';

/** Member meetings: joined by occurrence id, or by link code (then by the resolved occurrence). */
export function memberBackend(api: ApiClient, target: { occurrenceId: string } | { code: string }): MeetingBackend & { occurrenceId: () => string | null } {
  let occurrenceId: string | null = 'occurrenceId' in target ? target.occurrenceId : null;
  const remember = (ctx: JoinContext) => {
    occurrenceId = ctx.occurrence.id;
    return ctx;
  };
  const id = () => {
    if (!occurrenceId) throw new Error('Meeting not joined yet');
    return occurrenceId;
  };
  return {
    kind: 'member',
    occurrenceId: () => occurrenceId,
    join: async () => remember(occurrenceId ? await api.joinOccurrence(occurrenceId) : await api.joinByCode((target as { code: string }).code)),
    status: async () => remember(await api.occurrenceStatus(id())),
    rejoin: async () => remember(await api.joinOccurrence(id())),
    check: async () => {
      const o = await api.occurrence(id());
      if (o.occurrence.state === 'ended') return { result: 'ended' as const };
      if (o.me?.state === 'removed') return { result: 'removed' as const };
      if (o.me?.state === 'denied') return { result: 'declined' as const };
      return { result: 'ok' as const, hostPresent: o.hostPresent, handRaised: o.me?.handRaised ?? false };
    },
    leave: async () => {
      if (occurrenceId) await api.leaveOccurrence(occurrenceId);
    },
    leaveOnUnload: () => {
      if (!occurrenceId) return;
      // keepalive lets the request finish while the tab closes; same session cookie + CSRF token.
      void fetch(`/v1/occurrences/${occurrenceId}/leave`, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...(api.csrfToken ? { 'x-dw-csrf': api.csrfToken } : {}) },
        body: '{}',
      }).catch(() => undefined);
    },
    raiseHand: (raised) => api.raiseHand(id(), raised).then(() => undefined),
  };
}

/**
 * Guest meetings: a meeting-only session (HttpOnly cookie scoped to /v1/guest) for one occurrence.
 * `resume` reuses a still-valid guest session after a page refresh instead of asking again.
 */
export function guestBackend(api: ApiClient, code: string, displayName: string, resume = false): MeetingBackend {
  const explain = async (): Promise<EndReason> => {
    const p = await api.guestPreview(code).catch(() => null);
    return p?.state === 'ended' ? 'ended' : 'removed';
  };
  return {
    kind: 'guest',
    join: () => (resume ? api.guestStatus() : api.guestJoin(code, displayName)),
    status: () => api.guestStatus(),
    rejoin: () => api.guestStatus(),
    check: async () => {
      const s = await api.guestStatus();
      const r = s.result;
      if (r.status === 'denied') return { result: r.reason === 'not_invited' ? ('declined' as const) : r.reason };
      return { result: 'ok' as const, handRaised: s.me?.handRaised ?? false };
    },
    leave: () => api.guestLeave().then(() => undefined),
    raiseHand: (raised) => api.guestHand(raised).then(() => undefined),
    explainLostAccess: explain,
  };
}
